import * as Mutations from "@/store/mutations.type";
import * as Actions from "@/store/actions.type";
import apiClient from "@/common/gateway-api";

export default {
  /**
   *
   * @desc Action to change the blog operation
   * @param {string} status the operation type (create/update)
   *
   */
  [Actions.CHANGE_BLOG_OPERATION]({ commit }, status) {
    commit(Mutations.SET_BLOG_OPERATION, status);
  },
  /**
   *
   * @desc Action to clear blog page error messages
   *
   */
  [Actions.CLEAR_BLOG_ERRORS]({ commit }) {
    commit(Mutations.SET_BLOG_ERROR, "");
  },
  /**
   *
   * @desc Action to clear blog page success messages
   *
   */
  [Actions.CLEAR_BLOG_SUCCESS]({ commit }) {
    commit(Mutations.SET_BLOG_SUCCESS, "");
  },
  /**
   *
   * @desc Action to set blog page error messages
   *
   */
  [Actions.SET_BLOG_ERRORS]({ commit }, { error }) {
    commit(Mutations.SET_BLOG_ERROR, error);
  },
  /**
   *
   * @desc Action to fetch component attributes of each blog section
   * @param {number} componentId the id of the component
   * @param {number} index the index of the blog section
   *
   */
  [Actions.FETCH_BLOG_COMPONENT_ATTR](
    { commit },
    { region, componentId, index }
  ) {
    commit(Mutations.SET_BLOG_COMPONENT_LOADING, { status: true, index });
    apiClient
      .get(`/api/v1/woo/components/${componentId}/attributes`, {
        params: { region }
      })
      .then((res) => {
        commit(Mutations.SET_BLOG_COMPONENT_ATTR, {
          attr: res.data.data,
          index
        });
        commit(Mutations.SET_BLOG_COMPONENT_LOADING, { status: false, index });
      })
      .catch((error) => {
        commit(Mutations.SET_BLOG_ERROR, error.message);
        commit(Mutations.SET_BLOG_COMPONENT_LOADING, { status: false, index });
      });
  },
  /**
   *
   * @desc Action to fetch component instance of each blog section
   * @param {number} componentId the id of the component
   *
   */
  [Actions.FETCH_BLOG_COMPONENT_INSTANCE]({ commit }, { region, componentId }) {
    return new Promise((resolve, reject) => {
      commit(Mutations.SET_BLOG_INSTANCE_LOADING, {
        status: true,
        componentId
      });
      apiClient
        .get(`/api/v1/woo/components/${componentId}/instances`, {
          params: { region }
        })
        .then((res) => {
          commit(Mutations.SET_BLOG_COMPONENT_INSTANCE, {
            instance: res.data.data,
            componentId
          });
          commit(Mutations.SET_BLOG_INSTANCE_LOADING, {
            status: false,
            componentId
          });
          resolve(res);
        })
        .catch((error) => {
          commit(Mutations.SET_BLOG_ERROR, error.message);
          commit(Mutations.SET_BLOG_INSTANCE_LOADING, {
            status: false,
            componentId
          });
          reject(error);
        });
    });
  },
  /**
   *
   * @desc Action to create all component instances for blog page
   * @param {object} formData the data in blog create form
   *
   */
  async [Actions.CREATE_BLOG_COMPONENT_INSTANCE](
    { commit },
    { region, formData, shortCode, user }
  ) {
    return new Promise((resolve, reject) => {
      commit(Mutations.SET_INSTANCE_CREATE_LOADING, true);
      const newComponents = formData.components.filter((component) => {
        return component.shouldCreate === "yes";
      });
      const existComponents = formData.components.filter((component) => {
        return component.shouldCreate === "no";
      });

      // 1. create post in wordpress
      const createPostRequest = apiClient.post(
        "/api/v1/woo/components/posts",
        {
          title: formData.blog?.abi_blog_title || "No Title",
          content: shortCode
        },
        { params: { region } }
      );

      createPostRequest.then((res) => {
        formData.blog["abi_blog_post_id"] = res.data.id;
        // 2. create instance for 'abi_blog'
        const createBlogRequest = apiClient.post(
          `/api/v1/woo/components/${formData.id}/instances`,
          {
            formData: {
              attributes: formData.blog,
              instance_name: formData.instanceName
            },
            user
          },
          { params: { region } }
        );

        // 3. create instance for each blog section
        const createRequests = newComponents.map((item) => {
          return apiClient.post(
            `/api/v1/woo/components/${item.componentId}/instances`,
            {
              formData: {
                attributes: item.attributes,
                instance_name: `blog_${item.name}`
              },
              user
            },
            {
              params: { region }
            }
          );
        });

        // 4. update instance for each blog section
        const updateRequests = existComponents.map((item) => {
          return apiClient.put(
            `/api/v1/woo/components/instances/${item.id}`,
            {
              formData,
              user
            },
            { params: { region } }
          );
        });

        // send all requests
        Promise.all([...createRequests, ...updateRequests, createBlogRequest])
          .then(() => {
            commit(Mutations.SET_INSTANCE_CREATE_LOADING, false);
          })
          .catch((error) => {
            commit(Mutations.SET_BLOG_ERROR, error.message);
            commit(Mutations.SET_INSTANCE_CREATE_LOADING, false);
          })
          .then(() => {
            resolve();
          })
          .catch((error) => {
            commit(Mutations.SET_BLOG_ERROR, error.message);
            reject();
          });
      });
    });
  },
  async [Actions.DELETE_BLOG_POST]({ commit }, { region, postId }) {
    try {
      await apiClient.delete(`/api/v1/woo/components/posts/${postId}`, {
        params: region
      });
    } catch (error) {
      commit(Mutations.SET_BLOG_ERROR, error.message);
    }
  },
  /**
   *
   * @desc Action to duplicate blog instance
   * @param {object} formData the blog data
   *
   */
  [Actions.DUPLICATE_BLOG_COMPONENT_INSTANCE](
    { commit },
    { formData, instanceNames, region, componentId, user }
  ) {
    commit(Mutations.SET_INSTANCE_COPY_LOADING, true);
    let formDataCopy = JSON.parse(JSON.stringify(formData));

    // 1. Update the blog instance
    let childComponentArr = [];
    instanceNames.components.forEach((instanceName, index) => {
      let componentId = formData.components[index].componentId;
      childComponentArr.push(componentId + "|" + instanceName);
    });
    formDataCopy.blog.abi_blog_components = childComponentArr.join(",");

    // 2. create instance for blog component
    return new Promise((resolve, reject) => {
      const blogRequest = apiClient.post(
        `/api/v1/woo/components/${componentId}/instances`,
        {
          formData: {
            attributes: formDataCopy.blog,
            instance_name: instanceNames.blog
          },
          user
        },
        { params: { region } }
      );
      let childComponentRequests = [];
      formDataCopy.components.forEach((component, index) => {
        childComponentRequests.push(
          apiClient.post(
            `/api/v1/woo/components/${component.componentId}/instances`,
            {
              formData: {
                attributes: component.attributes,
                instance_name: instanceNames.components[index]
              },
              user
            },
            { params: { region } }
          )
        );
      });
      Promise.all([blogRequest, ...childComponentRequests])
        .then(() => {
          resolve();
          commit(Mutations.SET_INSTANCE_COPY_LOADING, false);
        })
        .catch(() => {
          reject();
          commit(Mutations.SET_INSTANCE_COPY_LOADING, false);
        });
    });
  },
  /**
   *
   * @desc Action to update all component instances for blog page
   * @param {object} formData the data in blog create form
   *
   */
  [Actions.UPDATE_BLOG_COMPONENT_INSTANCE](
    { commit },
    { region, formData, shortCode, user }
  ) {
    commit(Mutations.SET_INSTANCE_EDIT_LOADING, true);
    const newComponents = formData.components.filter((component) => {
      return component.shouldCreate === "yes";
    });
    const existComponents = formData.components.filter((component) => {
      return component.shouldCreate === "no";
    });

    // 1. update the instance for 'abi_blog'
    const updateBlogRequest = apiClient.put(
      `/api/v1/woo/components/instances/${formData.blogInstanceId}`,
      { formData: formData.blog, user },
      { params: { region } }
    );

    // 2. create post in wordpress
    const updatePostRequest = apiClient.put(
      `/api/v1/woo/components/posts/${formData.blog.abi_blog_post_id}`,
      {
        title: formData.blog?.abi_blog_title || "No Title",
        content: shortCode
      },
      { params: { region } }
    );

    // 3. create instance for each blog section
    const createRequests = newComponents.map((item) => {
      return apiClient.post(
        `/api/v1/woo/components/${item.componentId}/instances`,
        {
          formData: {
            attributes: item.attributes,
            instance_name: `blog_${item.name}`
          },
          user
        },
        { params: { region } }
      );
    });

    // 4. update instance for each blog section
    const updateRequests = existComponents.map((item) => {
      return apiClient.put(
        `/api/v1/woo/components/instances/${item.id}`,
        { formData: item.attributes, user },
        { params: { region } }
      );
    });

    // send all requests
    const requests = [...createRequests, ...updateRequests, updateBlogRequest];

    if (formData.blog.abi_blog_post_id) {
      requests.push(updatePostRequest);
    }

    Promise.all(requests)
      .then(() => {
        commit(Mutations.SET_INSTANCE_EDIT_LOADING, false);
        commit(Mutations.SET_BLOG_SUCCESS, "Blog Updated Success!");
        setTimeout(() => {
          commit(Mutations.SET_BLOG_SUCCESS, "");
        }, 5000);
      })
      .catch((error) => {
        console.log(error);
        commit(Mutations.SET_BLOG_ERROR, error.message);
        commit(Mutations.SET_INSTANCE_EDIT_LOADING, false);
      });
  },
  /**
   *
   * @desc Action to set the selected instances
   * @param {number} formData the id of the instance
   * @param {number} index the index of the blog section
   *
   */
  [Actions.SET_SELECTED_BLOG_INSTANCE]({ commit }, { instanceId, index }) {
    commit(Mutations.SET_BLOG_SELECTED_INSTANCE, { instanceId, index });
  },
  /**
   *
   * @desc Action to clear all fetched component attributes
   *
   */
  [Actions.CLEAR_BLOG_COMPONENT_ATTR]({ commit }) {
    commit(Mutations.CLEAR_BLOG_COMPONENT_ATTR);
  },
  /**
   *
   * @desc Action to clear all loading status
   *
   */
  [Actions.CLEAR_BLOG_COMPONENT_LOADING]({ commit }) {
    commit(Mutations.CLEAR_BLOG_COMPONENT_LOADING);
  },
  /**
   *
   * @desc Action to clear selected instance
   *
   */
  [Actions.CLEAR_BLOG_SELECTED_INSTANCE]({ commit }) {
    commit(Mutations.CLEAR_BLOG_SELECTED_INSTANCE);
  },
  /**
   *
   * @desc Action to delete selected instance
   *
   */
  [Actions.DELETE_SELECTED_INSTANCE]({ commit }, { index }) {
    commit(Mutations.DELETE_SELECTED_INSTANCE, { index });
  },
  /**
   *
   * @desc Action to delete selected instance
   *
   */
  [Actions.DELETE_COMPONENT_ATTRIBUTE]({ commit }, { index }) {
    commit(Mutations.DELETE_COMPONENT_ATTRIBUTE, { index });
  }
};
